import {
  type DocumentNode,
  type OperationVariables,
  useQuery,
} from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import {
  EXPORT_POLL_INTERVAL_MS,
  EXPORT_UNRECOGNIZED_STATE_TIMEOUT_MS,
} from './constants';

const DEFAULT_EXPORT_PROGRESS = 0;

export type PolledExport<TState extends string = string> = {
  id: string;
  progress?: number | null;
  state: TState;
  fileUrl?: string | null;
};

export type ExportPollStates<TState extends string = string> = {
  generating: TState;
  completed: TState;
  failed: TState;
};

type UseExportPollOptions<
  TData,
  TVariables extends OperationVariables,
  TState extends string = string,
> = {
  query: DocumentNode;
  variables: TVariables;
  /**
   * Mirrors `useQuery`'s `skip`, inverted: poll only while `true`. Call this
   * hook unconditionally and toggle `enabled` (e.g. `activeExportId !== null`)
   * rather than conditionally mounting/unmounting a wrapper component.
   */
  enabled: boolean;
  selectExport: (
    data: TData | undefined,
  ) => PolledExport<TState> | null | undefined;
  states: ExportPollStates<TState>;
  onComplete: (fileUrl: string) => void;
  onFail: () => void;
};

export type ExportPollResult = {
  /** 0–1 export progress, resetting to 0 whenever `enabled` is false. */
  progress: number;
};

/**
 * Polls an export status query until it reaches a terminal state.
 *
 * Call unconditionally and drive with `enabled`. The query is skipped while
 * disabled, and the internal completion/failure guard resets on every
 * disabled -> enabled transition, so a new export (a fresh `variables`
 * identity paired with `enabled` cycling false -> true) behaves like a clean
 * session without needing a remount.
 *
 * Callers must clear/null their export id (set `enabled` to `false`) before
 * starting a new export (`enabled` back to `true` with fresh `variables`).
 *
 * `progress` is returned rather than pushed through an `onProgress` callback
 * so callers don't need to mirror it into their own state (and reset it on
 * every terminal/dismiss/restart path) — it already reads 0 whenever
 * `enabled` is false.
 *
 * Terminal handling reads query `data` in `useEffect` rather than relying on
 * `useQuery`'s `onCompleted`, which Apollo Client 3.13+ no longer calls
 * reliably while polling (see apollographql/apollo-client#12352).
 */
export const useExportPoll = <
  TData,
  TVariables extends OperationVariables,
  TState extends string = string,
>({
  query,
  variables,
  enabled,
  selectExport,
  states,
  onComplete,
  onFail,
}: UseExportPollOptions<TData, TVariables, TState>): ExportPollResult => {
  const handledRef = useRef(false);
  const wasEnabledRef = useRef(false);

  const { data, error, loading, startPolling, stopPolling } = useQuery<
    TData,
    TVariables
  >(query, {
    variables,
    skip: !enabled,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (enabled && !wasEnabledRef.current) {
      handledRef.current = false;
    }
    wasEnabledRef.current = enabled;
  }, [enabled]);

  const polledExport = enabled ? selectExport(data) : undefined;
  const state = polledExport?.state;
  const fileUrl = polledExport?.fileUrl;
  const progress = polledExport?.progress ?? DEFAULT_EXPORT_PROGRESS;

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }
    startPolling(EXPORT_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [enabled, startPolling, stopPolling]);

  const fail = useCallback(() => {
    if (handledRef.current) return;
    handledRef.current = true;
    stopPolling();
    onFail();
  }, [onFail, stopPolling]);

  const complete = useCallback(
    (completedFileUrl: string) => {
      if (handledRef.current) return;
      handledRef.current = true;
      stopPolling();
      onComplete(completedFileUrl);
    },
    [onComplete, stopPolling],
  );

  useEffect(() => {
    if (!enabled || loading) return;
    if (!state || state === states.generating) {
      // GENERATING (including progress === 1, written before the backend's
      // S3 upload completes) keeps polling — only COMPLETED/FAILED are
      // terminal. An unrecognized state is handled by the timeout below.
      return;
    }

    if (state === states.completed) {
      if (fileUrl) {
        complete(fileUrl);
      } else {
        fail();
      }
      return;
    }

    if (state === states.failed) {
      fail();
    }
  }, [
    complete,
    enabled,
    fail,
    fileUrl,
    loading,
    state,
    states.completed,
    states.failed,
    states.generating,
  ]);

  useEffect(() => {
    const isUnrecognized =
      state != null &&
      state !== states.generating &&
      state !== states.completed &&
      state !== states.failed;

    if (!isUnrecognized) return;

    const timeoutId = setTimeout(fail, EXPORT_UNRECOGNIZED_STATE_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [fail, state, states.completed, states.failed, states.generating]);

  useEffect(() => {
    if (!enabled || loading || !error) return;
    if (state === states.completed && fileUrl) return; // a concurrent success wins
    fail();
  }, [enabled, error, fail, fileUrl, loading, state, states.completed]);

  return { progress };
};
